/**
 * =============================================================================
 * SPEQ55 RFP Pro - Pricing Management (Admin)
 * =============================================================================
 *
 * WHAT THIS FILE DOES
 * -------------------
 * Super-admin page for viewing and editing the per-model pricing table
 * that drives the Cost Monitoring dashboard. Shows current cost-per-1K
 * token rates for every Gemini model the product uses, lets an admin
 * update them inline, and displays an audit history of prior edits.
 *
 * WHY IT EXISTS
 * -------------
 * Google occasionally adjusts Gemini pricing. Rather than hard-coding
 * rates in the backend (which would require a deploy to update), we
 * persist them in a DB table and expose this page so an admin can keep
 * them current from the UI. Every change is audit-logged so we can tie
 * spend anomalies to pricing adjustments.
 *
 * DEPENDENCIES
 * ------------
 *   ../../api/pricing - thin axios wrappers for GET/PUT /api/admin/pricing
 *
 * DEPENDED ON BY
 * --------------
 *   frontend/src/App.jsx - mounted at /app/admin/pricing (super_admin only)
 *
 * @module pages/admin/PricingManagement
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import * as pricingApi from '../../api/pricing';

export default function PricingManagement() {
  const [pricing, setPricing] = useState([]);
  const [auditHistory, setAuditHistory] = useState([]);
  const [editingModel, setEditingModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [pricingRes, auditRes] = await Promise.all([
        pricingApi.getActivePricing(),
        pricingApi.getPricingAuditHistory(20),
      ]);

      setPricing(pricingRes.pricing);
      setAuditHistory(auditRes.audit);
    } catch {
      setError('Failed to load pricing data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdatePricing(serviceName, modelName, data) {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await pricingApi.updatePricing(serviceName, modelName, data);
      alert('Pricing updated successfully! Changes will take effect within 5 minutes.');
      setEditingModel(null);
      loadData();
    } catch (err) {
      alert(`Failed to update pricing: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading pricing data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">API Pricing Management</h1>
        <p className="text-gray-600 mt-1">Manage API pricing without code changes</p>
      </div>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <p className="text-sm text-yellow-700">
          <strong>Important:</strong> Pricing changes take effect within 5 minutes due to caching.
          All changes are audited and historical pricing is preserved for accurate cost reporting.
        </p>
      </div>

      {/* Current Pricing Table */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Current Pricing Configuration</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Model
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Input ($/1M)
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Output ($/1M)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Effective From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pricing.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{p.service_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{p.model_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    ${parseFloat(p.input_price_per_1m).toFixed(3)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    ${parseFloat(p.output_price_per_1m).toFixed(3)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        p.pricing_tier === 'free'
                          ? 'bg-green-100 text-green-800'
                          : p.pricing_tier === 'premium'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {p.pricing_tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(p.effective_from).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => setEditingModel(p)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingModel && (
        <EditPricingModal
          model={editingModel}
          onSave={handleUpdatePricing}
          onCancel={() => setEditingModel(null)}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Audit History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Recent Pricing Changes</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service/Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Changed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Reason
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {auditHistory.map((audit) => (
                <tr key={audit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(audit.changed_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {audit.service_name}/{audit.model_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        audit.action === 'created'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {audit.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {audit.first_name} {audit.last_name}
                  </td>
                  <td className="px-6 py-4 text-sm">{audit.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EditPricingModal({ model, onSave, onCancel, isSubmitting }) {
  const [formData, setFormData] = useState({
    inputPricePer1M: parseFloat(model.input_price_per_1m),
    outputPricePer1M: parseFloat(model.output_price_per_1m),
    pricingTier: model.pricing_tier,
    effectiveFrom: '',
    notes: '',
  });

  function handleSubmit(e) {
    e.preventDefault();
    if (!formData.notes.trim()) {
      alert('Please provide a reason for the pricing change');
      return;
    }
    onSave(model.service_name, model.model_name, formData);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">
          Update Pricing: {model.service_name}/{model.model_name}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Input Price ($/1M tokens)</label>
            <input
              type="number"
              step="0.001"
              min="0"
              value={formData.inputPricePer1M}
              onChange={(e) =>
                setFormData({ ...formData, inputPricePer1M: parseFloat(e.target.value) })
              }
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Output Price ($/1M tokens)</label>
            <input
              type="number"
              step="0.001"
              min="0"
              value={formData.outputPricePer1M}
              onChange={(e) =>
                setFormData({ ...formData, outputPricePer1M: parseFloat(e.target.value) })
              }
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Effective From (optional)</label>
            <input
              type="datetime-local"
              value={formData.effectiveFrom}
              onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank for immediate effect</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Reason for Change *</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows="3"
              placeholder="e.g., Google announced price change effective March 15, 2026"
              required
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Updating...' : 'Update Pricing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
